import type { Booking } from '@/components/BookingCalendar';
import { parseISO, isSameDay, addHours, format, isBefore, isAfter, addDays } from 'date-fns';

export type ConflictType = 'overlap' | 'adjacent' | 'same_client' | 'capacity';

export type ConflictSeverity = 'error' | 'warning';

export type BookingConflict = {
  type: ConflictType;
  severity: ConflictSeverity;
  message: string;
  booking: Booking;
  suggestedAlternatives: {
    date: string;
    time: string;
    room?: number;
  }[];
};

export function checkBookingConflicts(
  newBooking: Booking,
  existingBookings: Booking[],
  excludeId?: number
): BookingConflict[] {
  const conflicts: BookingConflict[] = [];

  // Check for time overlaps (Error)
  const overlappingBooking = findOverlappingBooking(newBooking, existingBookings, excludeId);
  if (overlappingBooking) {
    conflicts.push({
      type: 'overlap',
      severity: 'error',
      message: `Time slot conflicts with existing booking for ${overlappingBooking.clientName}`,
      booking: overlappingBooking,
      suggestedAlternatives: generateAlternatives(newBooking, existingBookings)
    });
  }

  // Check for adjacent bookings by same client (Warning)
  const adjacentBooking = findAdjacentBooking(newBooking, existingBookings, excludeId);
  if (adjacentBooking) {
    conflicts.push({
      type: 'adjacent',
      severity: 'warning',
      message: `${adjacentBooking.clientName} has another booking immediately before/after this time`,
      booking: adjacentBooking,
      suggestedAlternatives: generateAlternatives(newBooking, existingBookings)
    });
  }

  // Check for same client booking on same day (Warning)
  const sameClientBooking = findSameClientBooking(newBooking, existingBookings, excludeId);
  if (sameClientBooking) {
    conflicts.push({
      type: 'same_client',
      severity: 'warning',
      message: `${sameClientBooking.clientName} already has a booking on this day`,
      booking: sameClientBooking,
      suggestedAlternatives: generateAlternatives(newBooking, existingBookings)
    });
  }

  return conflicts;
}

function findOverlappingBooking(
  newBooking: Booking,
  existingBookings: Booking[],
  excludeId?: number
): Booking | null {
  return existingBookings.find(existing => {
    if (excludeId && existing.id === excludeId) return false;
    if (existing.roomId !== newBooking.roomId) return false;
    if (!isSameDay(parseISO(existing.date), parseISO(newBooking.date))) return false;

    const existingStart = parseISO(`${existing.date}T${existing.timeSlot}`);
    const existingEnd = addHours(existingStart, existing.duration);
    const newStart = parseISO(`${newBooking.date}T${newBooking.timeSlot}`);
    const newEnd = addHours(newStart, newBooking.duration);

    return (
      (isAfter(newStart, existingStart) && isBefore(newStart, existingEnd)) ||
      (isAfter(existingStart, newStart) && isBefore(existingStart, newEnd)) ||
      (isBefore(newStart, existingStart) && isAfter(newEnd, existingEnd)) ||
      (isBefore(existingStart, newStart) && isAfter(existingEnd, newEnd))
    );
  }) || null;
}

function findAdjacentBooking(
  newBooking: Booking,
  existingBookings: Booking[],
  excludeId?: number
): Booking | null {
  return existingBookings.find(existing => {
    if (excludeId && existing.id === excludeId) return false;
    if (existing.roomId !== newBooking.roomId) return false;
    if (!isSameDay(parseISO(existing.date), parseISO(newBooking.date))) return false;
    if (existing.clientName !== newBooking.clientName) return false;

    const existingStart = parseISO(`${existing.date}T${existing.timeSlot}`);
    const existingEnd = addHours(existingStart, existing.duration);
    const newStart = parseISO(`${newBooking.date}T${newBooking.timeSlot}`);
    const newEnd = addHours(newStart, newBooking.duration);

    return (
      format(existingEnd, 'HH:mm') === format(newStart, 'HH:mm') ||
      format(newEnd, 'HH:mm') === format(existingStart, 'HH:mm')
    );
  }) || null;
}

function findSameClientBooking(
  newBooking: Booking,
  existingBookings: Booking[],
  excludeId?: number
): Booking | null {
  return existingBookings.find(existing => {
    if (excludeId && existing.id === excludeId) return false;
    if (!isSameDay(parseISO(existing.date), parseISO(newBooking.date))) return false;
    return existing.clientName === newBooking.clientName;
  }) || null;
}

function generateAlternatives(
  booking: Booking,
  existingBookings: Booking[]
): { date: string; time: string; room?: number }[] {
  const alternatives: { date: string; time: string; room?: number }[] = [];
  const date = parseISO(booking.date);

  // Try same day, different times
  const timeSlots = [
    '09:00', '10:00', '11:00', '12:00', '13:00', '14:00',
    '15:00', '16:00', '17:00', '18:00', '19:00', '20:00',
    '21:00', '22:00', '23:00', '00:00', '01:00', '02:00'
  ];

  // Check each time slot
  for (const time of timeSlots) {
    const isAvailable = !existingBookings.some(existing => {
      if (existing.roomId !== booking.roomId) return false;
      if (!isSameDay(parseISO(existing.date), date)) return false;

      const existingStart = parseISO(`${existing.date}T${existing.timeSlot}`);
      const existingEnd = addHours(existingStart, existing.duration);
      const testStart = parseISO(`${format(date, 'yyyy-MM-dd')}T${time}`);
      const testEnd = addHours(testStart, booking.duration);

      return (
        (isAfter(testStart, existingStart) && isBefore(testStart, existingEnd)) ||
        (isAfter(existingStart, testStart) && isBefore(existingStart, testEnd))
      );
    });

    if (isAvailable) {
      alternatives.push({
        date: format(date, 'yyyy-MM-dd'),
        time
      });
    }

    // Limit to 3 alternatives per day
    if (alternatives.length >= 3) break;
  }

  // If we couldn't find alternatives on the same day, try next day
  if (alternatives.length === 0) {
    const nextDay = addDays(date, 1);
    alternatives.push({
      date: format(nextDay, 'yyyy-MM-dd'),
      time: '09:00'
    });
  }

  return alternatives;
}

export function validateBookingTime(booking: Booking): string | null {
  const startHour = parseInt(booking.timeSlot.split(':')[0]);
  
  // Validate full day bookings
  if (booking.duration === 14) {
    if (startHour !== 9) {
      return "Full day bookings must start at 09:00";
    }
    return null;
  }

  // Validate regular bookings
  if (startHour < 9 && startHour > 2) {
    return "Bookings can only start between 09:00-23:59 and 00:00-02:00";
  }

  const endHour = (startHour + booking.duration) % 24;
  if (endHour > 2 && endHour < 9) {
    return "Bookings cannot end between 02:00 and 09:00";
  }

  return null;
}